import React, { useEffect, useState } from 'react';
import './NavaratriPage.css';
import api from '../api';

export default function NavaratriPage() {
  const [config, setConfig] = useState(null);
  const [activeDate, setActiveDate] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState({});
  const [bookings, setBookings] = useState({});
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  useEffect(() => { loadConfig(); }, []);

  /**
   * Get the list of time slots for a given date (YYYY-MM-DD) from config.
   * Uses special slots for Wed Oct 14, 2026.
   */
  const getSlotsForDate = (date) => {
    return config.special_slots?.[date] ?? config.time_slots;
  };

  /**
   * Format a time slot string (e.g., "21:00") into a readable format (e.g., "9:00pm").
   * Removes leading zeros and converts to 12-hour PM format.
   */
  const formatSlotTime = (slot) => {
    // Remove leading zero from hour (e.g., "21:00" -> "9:00")
    const hour = parseInt(slot.split(':')[0], 10);
    const minute = slot.split(':')[1];
    // Convert to 12-hour format with AM/PM
    const hour12 = hour % 12;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour12 === 0 ? '12' : hour12;
    return `${formattedHour}:${minute} ${ampm}`;
  };

  const loadConfig = async () => {
    try {
      const res = await api.get('/api/navaratri/config');
      setConfig(res.data);
      loadAllBookings(res.data);
    } catch (err) {
      console.error('Failed to load config:', err);
      setMessage({ type: 'error', text: 'Failed to load event configuration' });
    }
  };

  const loadAllBookings = async (cfg) => {
    const configToUse = cfg || config;
    if (!configToUse) return;

    try {
      setLoading(true);
      const startDate = new Date(configToUse.start_date + 'T00:00:00');
      const endDate = new Date(configToUse.end_date + 'T00:00:00');

      const promises = [];
      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateStr = currentDate.toISOString().split('T')[0];
        promises.push(api.get(`/api/navaratri/slots?date=${dateStr}`));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      const results = await Promise.all(promises);
      const bookingsMap = {};

      currentDate = new Date(startDate);
      results.forEach((result, index) => {
        const dateStr = currentDate.toISOString().split('T')[0];
        bookingsMap[dateStr] = result.data;
        currentDate.setDate(currentDate.getDate() + 1);
      });

      // Apply special slots for the current date (e.g., Oct 14, 2026)
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      if (todayStr === configToUse.special_date) {
        bookingsMap[todayStr] = configToUse.special_slots[todayStr] ?? bookingsMap[todayStr];
      }

      setBookings(bookingsMap);
    } catch (err) {
      console.error('Failed to load bookings:', err);
      setMessage({ type: 'error', text: 'Failed to load booking information' });
    } finally {
      setLoading(false);
    }
  };

  const formatUserName = (name) => {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[1][0]}.`;
  };

  const refreshBookingsForDate = async (date) => {
    try {
      const res = await api.get(`/api/navaratri/slots?date=${date}`);
      setBookings(prev => ({ ...prev, [date]: res.data }));
    } catch (err) {
      console.error('Failed to refresh bookings for date:', err);
    }
  };

  const handleDateChange = (date) => {
    setActiveDate(date);
  };



  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

    const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if there are any selected slots
    if (Object.keys(selectedSlots).length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one slot' });
      return;
    }

    if (!formData.name.trim()) {
      setMessage({ type: 'error', text: 'Name is required' });
      return;
    }

    if (!isValidEmail(formData.email.trim())) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    if (!formData.phone.trim()) {
      setMessage({ type: 'error', text: 'Phone number is required' });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    // Book each selected slot one by one
    const dates = Object.keys(selectedSlots);
    let successCount = 0;
    let errorCount = 0;

    for (const date of dates) {
      const timeSlot = selectedSlots[date];

      try {
        const params = new URLSearchParams();
        params.append('date', date);
        params.append('time_slot', timeSlot);
        params.append('name', formData.name.trim());
        params.append('email', formData.email.trim().toLowerCase());
        params.append('phone', formData.phone.trim());

        await api.post('/api/navaratri/book', params);
        successCount++;
        
        // Refresh bookings for this date to update the slot status
        await refreshBookingsForDate(date);
      } catch (err) {
        errorCount++;
      }
    }

    if (successCount > 0 && errorCount === 0) {
      setMessage({ type: 'success', text: `Successfully booked ${successCount} slot(s).` });
      // Clear selected slots only on complete success
      setSelectedSlots({});
    } else if (errorCount > 0) {
      setMessage({ type: 'error', text: `${errorCount} booking(s) failed. You can book only one slot per day. Please select another slot and try again.` });
      // Keep form and selected slots intact on error so user can retry
    }

    setSubmitting(false);
  };

  const getDayName = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const getMonthDay = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getFullYear = (dateStr) => {
    const [year] = dateStr.split('-');
    return year;
  };

  const generateDateRange = (startDateStr, endDateStr) => {
    const startDate = new Date(startDateStr + 'T00:00:00');
    const endDate = new Date(endDateStr + 'T00:00:00');
    const dates = [];
    let current = new Date(startDate);
    while (current <= endDate) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  if (!config) {
    return (
      <div className="navaratri-page">
        <div className="navaratri-panel">
          <div className="nav-loading">Loading Navaratri Special Readings...</div>
        </div>
      </div>
    );
  }

  const allDates = generateDateRange(config.start_date, config.end_date);

  return (
    <div className="navaratri-page">
      <div className="navaratri-panel">
        <h1 className="nav-title">Navaratri Special Readings(Online)</h1>

        <div className="event-image-wrapper">
          <img src={config.image_url} alt="Navaratri Special Readings" className="event-image" />
        </div>

        <p className="description" dangerouslySetInnerHTML={{ __html: config.description }} />

        <div className="date-grid">
          <div className="date-header">
            📅 {getMonthDay(config.start_date)} – {getMonthDay(config.end_date)}, {getFullYear(config.start_date)}
          </div>

          <div className="date-row">
            {allDates.map((dateStr) => {
              const isSelected = activeDate === dateStr;
              const dayName = getDayName(dateStr);
              const monthDay = getMonthDay(dateStr);

              return (
                <div
                  key={dateStr}
                  className={`date-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleDateChange(dateStr)}
                >
                  <div className="date-card-day">{dayName}</div>
                  <div className="date-card-date">{monthDay}</div>
                  <div className="time-slots-row">
                    {getSlotsForDate(dateStr).map((slot) => {
                      const slotData = bookings[dateStr]?.find(s => s.time_slot === slot) || {
                        time_slot: slot,
                        user_name: null,
                        is_booked: false
                      };

                      const isBooked = slotData.is_booked;
                      const userName = slotData.user_name;
                      const isSelectedSlot = selectedSlots[dateStr] === slot;

                      return (
                        <div
                          key={slot}
                          className={`time-slot ${isBooked ? 'booked' : 'available'} ${isSelectedSlot ? 'selected-time-slot' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isBooked) {
                              if (isSelectedSlot) {
                                // De-select if already selected
                                const newSlots = { ...selectedSlots };
                                delete newSlots[dateStr];
                                setSelectedSlots(newSlots);
                              } else {
                                // Select the slot
                                setSelectedSlots(prev => ({
                                  ...prev,
                                  [dateStr]: slot
                                }));
                                setActiveDate(dateStr);
                              }
                            }
                          }}
                        >
                          {isBooked ? (
                            <div className="slot-reserved">
                              <span className="user-name">{formatUserName(userName)}</span>
                              <span className="slot-time-text">{formatSlotTime(slot)}</span>
                            </div>
                          ) : (
                            <span className="slot-time">{formatSlotTime(slot)}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {Object.keys(selectedSlots).length > 0 && (
          <div className="booking-form-section">
            <h2 className="booking-heading">Book Selected Slots</h2>
            <p className="selected-slot-info">
              You have selected {Object.keys(selectedSlots).length} slot(s):
            </p>
            <div className="selected-slots-list">
              {Object.entries(selectedSlots).map(([date, slot]) => (
                <div key={date} className="selected-slot-item">
                  <span>
                    {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}{' '}
                    at {formatSlotTime(slot)}
                  </span>
                  <button
                    type="button"
                    className="remove-slot-btn"
                    onClick={() => {
                      const newSlots = { ...selectedSlots };
                      delete newSlots[date];
                      setSelectedSlots(newSlots);
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="booking-form" noValidate>
              <div className="form-group">
                <label htmlFor="nav-name">Name</label>
                <input
                  id="nav-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  autoComplete="name"
                  ariaRequired="true"
                />
              </div>

              <div className="form-group">
                <label htmlFor="nav-email">Email <span className="req">*</span></label>
                <input
                  id="nav-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="nav-phone">Phone <span className="req">*</span></label>
                <input
                  id="nav-phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  autoComplete="tel"
                />
              </div>

              <button type="submit" className="button-primary" disabled={submitting}>
                {submitting ? 'Booking…' : `Book ${Object.keys(selectedSlots).length} Slot(s)`}
              </button>
            </form>
          </div>
        )}

        {message.text && (
          <div className={`msg msg-${message.type}`} role="alert">
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
}
